import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Box, Paper, Typography, Grid } from '@mui/material';
import { fK, fX, fDate, resultColor, } from '../utils/formatters';
import { aggregateAds } from '../utils/scopeAggregator';
function buildCounts(ads) {
    const c = {
        Pass: 0,
        Optimize: 0,
        Fail: 0,
        Learning: 0,
        'No Benchmark': 0,
    };
    for (const a of ads || []) {
        const r = a.result;
        if (r in c)
            c[r]++;
    }
    return c;
}
// KPI card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, topColor, valueColor }) => (_jsxs(Paper, { elevation: 0, sx: {
        p: 2,
        border: '1px solid #EAECF0',
        borderTop: topColor ? `3px solid ${topColor}` : undefined,
        borderRadius: '8px',
        bgcolor: '#fff',
        height: '100%',
    }, children: [_jsx(Typography, { sx: {
                fontSize: 10,
                color: '#98A2B3',
                fontWeight: 700,
                letterSpacing: '0.08em',
                mb: 1,
                textTransform: 'uppercase',
            }, children: label }), _jsx(Typography, { sx: {
                fontSize: 26,
                fontWeight: 700,
                color: valueColor || '#101828',
                letterSpacing: '-0.02em',
                lineHeight: 1,
            }, children: value }), sub && (_jsx(Typography, { sx: { fontSize: 11, color: '#98A2B3', mt: 1 }, children: sub }))] }));
// Wasted spend cell with percent bar
const WastedCell = ({ label, value, color, pct, sub, bordered }) => (_jsxs(Box, { sx: {
        p: 2.5,
        borderRight: bordered ? '1px solid #EAECF0' : 'none',
    }, children: [_jsx(Typography, { sx: {
                fontSize: 11,
                color,
                fontWeight: 700,
                letterSpacing: '0.03em',
                textTransform: 'uppercase',
                mb: 1,
            }, children: label }), _jsx(Typography, { sx: {
                fontSize: 26,
                fontWeight: 800,
                color,
                lineHeight: 1,
                letterSpacing: '-0.02em',
            }, children: value }), pct != null && (_jsxs(Box, { sx: { mt: 1.25 }, children: [_jsx(Box, { sx: {
                        bgcolor: '#F2F4F7',
                        borderRadius: 3,
                        height: 3,
                        mb: 0.5,
                    }, children: _jsx(Box, { sx: {
                            bgcolor: color,
                            width: `${Math.min(pct, 100)}%`,
                            height: 3,
                            borderRadius: 3,
                        } }) }), _jsxs(Typography, { sx: {
                        fontSize: 11,
                        color,
                        fontWeight: 500,
                        opacity: 0.7,
                    }, children: [pct, "% of total spend"] })] })), sub && pct == null && (_jsx(Typography, { sx: { fontSize: 11, color, fontWeight: 600, mt: 1.25 }, children: sub }))] }));
// ── Main ─────────────────────────────────────────────────────────────────
const SummaryTab = ({ report }) => {
    const [scope, setScope] = useState('all');
    const ads = report.ads || [];
    const counts = buildCounts(ads);
    const evaluated = counts.Pass + counts.Optimize + counts.Fail;
    // Active projections — saved (all-time) or recomputed (period-bound)
    const periodAgg = useMemo(() => aggregateAds(ads, 'period', report.period_start, report.period_end), [ads, report.period_start, report.period_end]);
    const p = scope === 'all' ? (report.projections || {}) : periodAgg;
    const pct = (n) => evaluated ? Math.round((n / evaluated) * 100) : 0;
    const pctOfAll = (n) => ads.length ? Math.round((n / ads.length) * 100) : 0;
    const wastedPct = (n) => p.totalSpend > 0 ? Math.round((n / p.totalSpend) * 100) : 0;
    return (_jsxs(Box, { sx: { p: 3 }, children: [_jsxs(Box, { sx: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                }, children: [_jsx(Box, { sx: {
                            display: 'inline-flex',
                            gap: 0.5,
                            p: 0.5,
                            bgcolor: '#F2F4F7',
                            borderRadius: '8px',
                        }, children: ['all', 'period'].map((s) => (_jsx(Box, { onClick: () => setScope(s), sx: {
                                px: 2,
                                py: 0.75,
                                fontSize: 12,
                                fontWeight: scope === s ? 700 : 500,
                                color: scope === s ? '#101828' : '#475467',
                                bgcolor: scope === s ? '#fff' : 'transparent',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                userSelect: 'none',
                                boxShadow: scope === s ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                                transition: 'all 0.15s',
                            }, children: s === 'all' ? 'All-time / Lifetime' : 'Report Period' }, s))) }), scope === 'period' && (_jsxs(Typography, { sx: { fontSize: 11, color: '#667085' }, children: ["Showing ", periodAgg.ads_in_scope, " of ", ads.length, " ads (minDate within", ' ', fDate(report.period_start), " \u2013 ", fDate(report.period_end), ")"] }))] }), _jsxs(Grid, { container: true, spacing: 1.25, sx: { mb: 1.5 }, children: [_jsx(Grid, { size: { xs: 12, sm: 6, md: 2 }, children: _jsx(StatCard, { label: "Total Ads", value: ads.length, sub: `${evaluated} evaluated`, topColor: "#C8C8D8" }) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: 2 }, children: _jsx(StatCard, { label: "Pass", value: counts.Pass, sub: `${pct(counts.Pass)}% of evaluated`, topColor: resultColor('Pass'), valueColor: resultColor('Pass') }) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: 2 }, children: _jsx(StatCard, { label: "Optimize", value: counts.Optimize, sub: `${pct(counts.Optimize)}% of evaluated`, topColor: resultColor('Optimize'), valueColor: resultColor('Optimize') }) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: 2 }, children: _jsx(StatCard, { label: "Fail", value: counts.Fail, sub: `${pct(counts.Fail)}% of evaluated`, topColor: resultColor('Fail'), valueColor: resultColor('Fail') }) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: 2 }, children: _jsx(StatCard, { label: "Learning", value: counts.Learning, sub: `${pctOfAll(counts.Learning)}% of total`, topColor: resultColor('Learning'), valueColor: resultColor('Learning') }) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: 2 }, children: _jsx(StatCard, { label: "No Benchmark", value: counts['No Benchmark'] || 0, sub: "no benchmark data", topColor: resultColor('No Benchmark'), valueColor: resultColor('No Benchmark') }) })] }), _jsxs(Grid, { container: true, spacing: 1.25, sx: { mb: 2.5 }, children: [_jsx(Grid, { size: { xs: 12, md: 4 }, children: _jsxs(Paper, { elevation: 0, sx: {
                                p: 2.5,
                                border: '1px solid #EAECF0',
                                borderLeft: '4px solid #EE2A7B',
                                borderRadius: '8px',
                                bgcolor: '#fff',
                            }, children: [_jsx(Typography, { sx: {
                                        fontSize: 11,
                                        color: '#98A2B3',
                                        fontWeight: 700,
                                        letterSpacing: '0.08em',
                                        mb: 1,
                                        textTransform: 'uppercase',
                                    }, children: "Actual Spend" }), _jsx(Typography, { sx: {
                                        fontSize: 26,
                                        fontWeight: 800,
                                        color: '#101828',
                                        letterSpacing: '-0.02em',
                                        lineHeight: 1,
                                    }, children: fK(p.totalSpend) })] }) }), _jsx(Grid, { size: { xs: 12, md: 4 }, children: _jsxs(Paper, { elevation: 0, sx: {
                                p: 2.5,
                                border: '1px solid #EAECF0',
                                borderLeft: '4px solid #027A48',
                                borderRadius: '8px',
                                bgcolor: '#fff',
                            }, children: [_jsx(Typography, { sx: {
                                        fontSize: 11,
                                        color: '#98A2B3',
                                        fontWeight: 700,
                                        letterSpacing: '0.08em',
                                        mb: 1,
                                        textTransform: 'uppercase',
                                    }, children: "Actual Revenue" }), _jsx(Typography, { sx: {
                                        fontSize: 26,
                                        fontWeight: 800,
                                        color: '#101828',
                                        letterSpacing: '-0.02em',
                                        lineHeight: 1,
                                    }, children: fK(p.totalRevenue) })] }) }), _jsx(Grid, { size: { xs: 12, md: 4 }, children: _jsxs(Paper, { elevation: 0, sx: {
                                p: 2.5,
                                border: '1px solid #EAECF0',
                                borderLeft: '4px solid #EE2A7B',
                                borderRadius: '8px',
                                bgcolor: '#fff',
                            }, children: [_jsx(Typography, { sx: {
                                        fontSize: 11,
                                        color: '#98A2B3',
                                        fontWeight: 700,
                                        letterSpacing: '0.08em',
                                        mb: 1,
                                        textTransform: 'uppercase',
                                    }, children: "Actual ROAS" }), _jsx(Typography, { sx: {
                                        fontSize: 26,
                                        fontWeight: 800,
                                        color: '#101828',
                                        letterSpacing: '-0.02em',
                                        lineHeight: 1,
                                    }, children: fX(p.actualROAS) })] }) })] }), _jsxs(Paper, { elevation: 0, sx: {
                    border: '1px solid #EAECF0',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    bgcolor: '#fff',
                }, children: [_jsxs(Box, { sx: {
                            p: 2,
                            borderBottom: '1px solid #EAECF0',
                            bgcolor: '#FAFAFA',
                        }, children: [_jsx(Typography, { sx: { fontWeight: 700, fontSize: 13, color: '#101828' }, children: "Spend on Ads That Should Be Off" }), _jsx(Typography, { sx: { fontSize: 11, color: '#667085', mt: 0.25 }, children: "Post-threshold spend on underperforming ads" })] }), _jsxs(Grid, { container: true, children: [_jsx(Grid, { size: { xs: 12, sm: 6, md: 3 }, children: _jsx(WastedCell, { label: "Wasted Spend (Failed Ads)", value: fK(p.failWastedSpend), color: resultColor('Fail'), pct: wastedPct(p.failWastedSpend || 0), bordered: true }) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: 3 }, children: _jsx(WastedCell, { label: "Wasted Spend (Optimized Ads)", value: fK(p.optWastedSpend), color: resultColor('Optimize'), pct: wastedPct(p.optWastedSpend || 0), bordered: true }) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: 3 }, children: _jsx(WastedCell, { label: "Wasted Spend (Fail + Optimized Ads)", value: fK(p.failOptWastedSpend), color: "#9F1239", pct: wastedPct(p.failOptWastedSpend || 0), bordered: true }) }), _jsx(Grid, { size: { xs: 12, sm: 6, md: 3 }, children: _jsx(WastedCell, { label: "Avg Pass ROAS", value: fX(p.avgPassROAS), color: resultColor('Pass'), pct: null, sub: `${counts.Pass} pass ads`, bordered: false }) })] })] })] }));
};
export default SummaryTab;
